import { useState, useMemo, useRef } from "react";
import Spinner from "../../../globalComponents/ui/Spinner";
import KpiPickerList from "./KpiPickerList";
import { useSaveMappings } from "./useSaveMappings";
import { buildMapping, getSourceLabel, getUnitLabel, kpiAssetKind, titleExistsIn } from "./helpers";

/** Metadata for the asset kind option cards (chooser + locked display). */
const KIND_META = {
  location: { icon: "fa-map-marker-alt", title: "Asset", subtitle: "Tied to a specific location / sub-location." },
  global: { icon: "fa-globe", title: "Global Asset", subtitle: "Global to the KPI, no location." },
};

function KindCard({ kind, onClick, disabled }) {
  const m = KIND_META[kind];
  return (
    <button
      type="button"
      className={`kpa-kind-card kpa-kind-card--${kind}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? "Locked: all entries for this KPI must use the same scope." : undefined}
    >
      <span className="kpa-kind-card-title">
        <i className={`fas ${m.icon}`} style={{ marginRight: 5, fontSize: 11 }} aria-hidden="true" />
        {m.title}
        {disabled && <i className="fas fa-lock" style={{ marginLeft: 8, fontSize: 10, color: "var(--kpa-slate-400)" }} aria-hidden="true" />}
      </span>
      <span className="kpa-kind-card-subtitle">{m.subtitle}</span>
    </button>
  );
}

/**
 * React 16 has no useId — a module counter keeps label/control ids unique
 * when several panels are mounted at once.
 */
let panelInstanceCounter = 0;

/**
 * Add-asset panel: pick scope, tag multiple names, save in one call.
 *
 * `candidates`: [{ kpiId, kpiTitle, assets }] — KPI rows pass one entry. In
 * single-KPI mode the scope is pinned by whatever is already saved for the
 * KPI (all assets must share one scope); `internalKind` is only consulted
 * when nothing pins it.
 *
 * `withKpiPicker` (asset view's page-level "Add Asset") shows a searchable
 * KPI checkbox list instead of targeting the candidates directly; saving is
 * blocked until at least one KPI is ticked.
 */
export default function AddAssetPanel({
  candidates,
  selectedFYId,
  sources,
  onSaved,
  onClose,
  withKpiPicker = false,
  pagePanel = false,
}) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `kpa-panel-${++panelInstanceCounter}`;
  const idBase = idRef.current;

  const safeSources = useMemo(() => sources || [], [sources]);
  const safeCandidates = useMemo(() => candidates || [], [candidates]);

  const lockedKind = withKpiPicker ? null : kpiAssetKind(safeCandidates[0]?.assets);

  const [internalKind, setInternalKind] = useState(null);
  const isLocationRequired = lockedKind !== null ? lockedKind === "location" : internalKind;

  const kindChosen = isLocationRequired !== null;
  const chosenKind = isLocationRequired ? "location" : "global";

  const [selectedKpiIds, setSelectedKpiIds] = useState(() => new Set());

  /** KPIs already locked to the other scope can't receive these assets. */
  const pickerCandidates = useMemo(() => {
    if (!withKpiPicker || !kindChosen) return [];
    return safeCandidates.map(c => {
      const k = kpiAssetKind(c.assets);
      return {
        kpiId: c.kpiId,
        kpiTitle: c.kpiTitle,
        tags: c.tags,
        locked: !!k && k !== chosenKind,
        lockedLabel: chosenKind === "location" ? "global only" : "location only",
      };
    });
  }, [withKpiPicker, kindChosen, safeCandidates, chosenKind]);

  const toggleKpi = (kpiId) => setSelectedKpiIds(prev => {
    const next = new Set(prev);
    if (next.has(kpiId)) next.delete(kpiId); else next.add(kpiId);
    return next;
  });

  const selectedTargets = useMemo(
    () => (withKpiPicker ? safeCandidates.filter(c => selectedKpiIds.has(c.kpiId)) : safeCandidates),
    [withKpiPicker, safeCandidates, selectedKpiIds],
  );
  const targetsReady = selectedTargets.length > 0;

  const [sourceId, setSourceId] = useState(null);
  const [subLocationId, setSubLocationId] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [pendingNames, setPendingNames] = useState([]);
  const [dupError, setDupError] = useState("");
  const { saving, saveError, save } = useSaveMappings(selectedFYId, onSaved, onClose);
  const inputRef = useRef(null);

  const subLocationOptions = useMemo(() => {
    if (!sourceId) return [];
    return safeSources.find(s => String(s.id) === String(sourceId))?.subLocation || [];
  }, [safeSources, sourceId]);

  const selectedSourceHasSubs = subLocationOptions.length > 0;
  const unitReady = !!(!isLocationRequired || (sourceId && (!selectedSourceHasSubs || subLocationId)));

  const sourceObj = safeSources.find(s => String(s.id) === String(sourceId));
  const subObj = subLocationOptions.find(s => String(s.id) === String(subLocationId));
  const unitLabel = isLocationRequired
    ? (unitReady ? getUnitLabel(sourceObj, subObj) : null)
    : "No location";

  const bucket = { isLocationRequired: !!isLocationRequired, sourceId, subLocationId };

  /**
   * Staged names are only valid for one scope + unit, so any change to either
   * clears them — handled in the event handlers, not in effects.
   */
  const clearStaged = () => {
    setPendingNames([]);
    setDupError("");
    setNameInput("");
  };

  /**
   * Applies a scope choice; also prunes ticked KPIs that are incompatible
   * with the new scope — in the event handler, so there is no intermediate
   * render with a bad selection.
   */
  const handleKindSelect = (next) => {
    setInternalKind(next);
    if (next !== true) {
      setSourceId(null);
      setSubLocationId(null);
    }
    if (withKpiPicker) {
      if (next === null) {
        setSelectedKpiIds(new Set());
      } else {
        const chosen = next ? "location" : "global";
        setSelectedKpiIds(prev => new Set([...prev].filter(id => {
          const k = kpiAssetKind(safeCandidates.find(c => c.kpiId === id)?.assets);
          return !k || k === chosen;
        })));
      }
    }
    clearStaged();
  };

  const handleSourceChange = (e) => {
    const v = e.target.value;
    setSourceId(v ? Number(v) : null);
    setSubLocationId(null);
    clearStaged();
  };

  const handleSubLocationChange = (e) => {
    const v = e.target.value;
    setSubLocationId(v ? Number(v) : null);
    clearStaged();
  };

  /**
   * (name, KPI) pairs that already exist — saved anyway for the other KPIs,
   * the duplicates are just skipped, and we tell the user up front.
   *
   * `b` deliberately re-derives the render-scope `bucket` from its three
   * primitives: `bucket` is a fresh object every render, so depending on it
   * would recompute this memo on every render. Don't "dedupe" `b` into
   * `bucket` — keep the deps primitive.
   */
  const skipsByName = useMemo(() => {
    const b = { isLocationRequired: !!isLocationRequired, sourceId, subLocationId };
    const out = {};
    for (const name of pendingNames) {
      const skipped = selectedTargets
        .filter(t => titleExistsIn(t.assets, name, b))
        .map(t => t.kpiTitle);
      if (skipped.length) out[name] = skipped;
    }
    return out;
  }, [pendingNames, selectedTargets, isLocationRequired, sourceId, subLocationId]);

  const skipCount = Object.values(skipsByName).reduce((n, arr) => n + arr.length, 0);
  const totalToSave = pendingNames.length * selectedTargets.length - skipCount;

  /**
   * Stages a name. Only blocks when the name exists in EVERY selected KPI
   * (nothing to add); partial overlaps are staged and reported as skips.
   */
  const addName = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    if (pendingNames.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
      setDupError(`"${trimmed}" is already in the list above.`);
      return;
    }
    const dupIn = selectedTargets.filter(t => titleExistsIn(t.assets, trimmed, bucket));
    if (targetsReady && dupIn.length === selectedTargets.length) {
      setDupError(
        isLocationRequired
          ? `An asset named "${trimmed}" already exists for this location.`
          : `A global asset named "${trimmed}" already exists for this KPI.`
      );
      return;
    }
    setDupError("");
    setPendingNames(prev => [...prev, trimmed]);
    setNameInput("");
    inputRef.current?.focus();
  };

  const removeName = (name) => {
    setPendingNames(prev => prev.filter(n => n !== name));
    setDupError("");
  };

  const handleSave = () => {
    if (!pendingNames.length || !targetsReady) return;
    if (isLocationRequired && !sourceId) return;

    const mappings = [];
    for (const assetTitle of pendingNames) {
      for (const t of selectedTargets) {
        if (titleExistsIn(t.assets, assetTitle, bucket)) continue;
        mappings.push(buildMapping({ kpiId: t.kpiId, assetTitle, isLocationRequired, sourceId, subLocationId }));
      }
    }
    save(mappings);
  };

  const canSave = !!(!saving && selectedFYId && unitReady && (!isLocationRequired || sourceId) && targetsReady && totalToSave > 0);
  const canStage = !!(nameInput.trim() && unitReady && targetsReady && !saving);

  return (
    <div className={`kpa-panel${pagePanel ? " kpa-panel--page" : ""}`}>
      <div className="kpa-panel-header">
        <span className="kpa-panel-title">
          {withKpiPicker ? "Add Asset to KPIs" : !kindChosen ? "Add Entries" : isLocationRequired ? "Add Assets" : "Add Global Assets"}
        </span>
        <button type="button" className="kpa-panel-close" onClick={onClose} aria-label="Close add panel">✕</button>
      </div>

      <div className="kpa-panel-body">
        {!kindChosen ? (
          <>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--kpa-slate-600)", marginBottom: 8 }}>
              <i className="fas fa-hand-pointer" style={{ marginRight: 6, color: "var(--kpa-primary)" }} aria-hidden="true" />
              {withKpiPicker ? "Choose what you want to add" : "Choose what you want to add for this KPI"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 4 }}>
              <KindCard kind="location" onClick={() => handleKindSelect(true)} />
              <KindCard kind="global" onClick={() => handleKindSelect(false)} />
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <KindCard kind={chosenKind} disabled />
              </div>
              {withKpiPicker && (
                <button type="button" className="kpa-btn kpa-btn--ghost" style={{ flexShrink: 0 }} onClick={() => handleKindSelect(null)}>
                  Change
                </button>
              )}
            </div>

            {isLocationRequired && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div>
                  <label className="kpa-label" htmlFor={`${idBase}-location`}>Location *</label>
                  <select
                    id={`${idBase}-location`}
                    className={`kpa-select${sourceId ? " kpa-select--filled" : ""}`}
                    value={sourceId ?? ""}
                    onChange={handleSourceChange}
                  >
                    <option value="">Select location…</option>
                    {safeSources.map(s => <option key={s.id} value={s.id}>{getSourceLabel(s)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="kpa-label" htmlFor={`${idBase}-sublocation`}>
                    Sub-location {selectedSourceHasSubs ? "*" : ""}
                  </label>
                  <select
                    id={`${idBase}-sublocation`}
                    className={`kpa-select${subLocationId ? " kpa-select--filled" : ""}`}
                    value={subLocationId ?? ""}
                    onChange={handleSubLocationChange}
                    disabled={!sourceId || subLocationOptions.length === 0}
                  >
                    <option value="">
                      {!sourceId ? "Select location first" : subLocationOptions.length === 0 ? "No sub-locations" : "Select sub-location…"}
                    </option>
                    {subLocationOptions.map(s => <option key={s.id} value={s.id}>{s.subLocation}</option>)}
                  </select>
                </div>
              </div>
            )}

            {unitLabel && (
              <div className={`kpa-unit-pill kpa-unit-pill--${chosenKind}`}>
                <i className={`fas ${isLocationRequired ? "fa-map-marker-alt" : "fa-globe"}`} aria-hidden="true" />
                <span>{unitLabel}</span>
              </div>
            )}

            {withKpiPicker && (
              <div style={{ marginBottom: 10 }}>
                <span className="kpa-label">KPIs *</span>
                <KpiPickerList
                  candidates={pickerCandidates}
                  selectedIds={selectedKpiIds}
                  onToggle={toggleKpi}
                  disabled={saving}
                  idBase={idBase}
                />
                <div className={`kpa-selection-strip${targetsReady ? " kpa-selection-strip--ready" : ""}`} style={{ marginTop: 8, marginBottom: 0 }} role="status">
                  <i className={`fas ${targetsReady ? "fa-check-square" : "fa-hand-pointer"}`} aria-hidden="true" />
                  <span>
                    {targetsReady
                      ? `${selectedTargets.length} KPI${selectedTargets.length !== 1 ? "s" : ""} selected`
                      : "Tick at least one KPI to add this asset to."}
                  </span>
                </div>
              </div>
            )}

            {pendingNames.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {pendingNames.map(name => (
                  <span key={name} className="kpa-chip">
                    {skipsByName[name] && (
                      <i
                        className="fas fa-exclamation-triangle"
                        style={{ fontSize: 9, color: "var(--kpa-amber-600)" }}
                        title={`Already exists in: ${skipsByName[name].join(", ")}`}
                      />
                    )}
                    {name}
                    <button type="button" className="kpa-chip-x" onClick={() => removeName(name)} aria-label={`Remove ${name}`}>×</button>
                  </span>
                ))}
              </div>
            )}

            {skipCount > 0 && (
              <div className="kpa-warn-box" role="status">
                <i className="fas fa-info-circle" aria-hidden="true" />
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>Already existing — will be skipped:</div>
                  {Object.entries(skipsByName).map(([name, kpis]) => (
                    <div key={name}>“{name}” in {kpis.join(", ")}</div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="kpa-label" htmlFor={`${idBase}-name`}>
                Asset Names * <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— press Enter or + to stage, then Save Assets</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <input
                    id={`${idBase}-name`}
                    ref={inputRef}
                    className={`kpa-input${dupError ? " kpa-input--error" : nameInput.trim() ? " kpa-input--active" : ""}`}
                    value={nameInput}
                    onChange={e => { setNameInput(e.target.value); if (dupError) setDupError(""); }}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addName(); } }}
                    placeholder={withKpiPicker && !targetsReady ? "Tick KPIs above first…" : unitReady ? "e.g. DG Stack Bangalore 01" : (isLocationRequired ? "Select location first…" : "Asset name…")}
                    disabled={!unitReady || !targetsReady || saving}
                  />
                </div>
                <button type="button" className="kpa-btn kpa-btn--stage" onClick={addName} disabled={!canStage}>
                  + Add
                </button>
              </div>

              {(dupError || saveError) && (
                <div className="kpa-error-box" role="alert">
                  <i className="fas fa-exclamation-circle" aria-hidden="true" />
                  <span>{dupError || saveError}</span>
                </div>
              )}
            </div>

            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--kpa-slate-100)", display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="kpa-btn kpa-btn--primary" onClick={handleSave} disabled={!canSave}>
                {saving
                  ? <><Spinner size={11} color="#fff" style={{ marginRight: 6 }} />Saving…</>
                  : <>
                    <i className="fas fa-save" style={{ marginRight: 5 }} aria-hidden="true" />
                    Save {totalToSave > 0 ? `${totalToSave} ` : ""}{isLocationRequired ? "Asset" : "Global Asset"}{totalToSave !== 1 ? "s" : ""}
                    {withKpiPicker && selectedTargets.length > 1 ? ` across ${selectedTargets.length} KPIs` : ""}
                  </>
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
